import { Modal, Tabs, message, notification } from 'antd';
import { isEmpty, isNil } from 'lodash';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EngineTab, permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { ENGINE_VERSION_1_0, EngineConfigStatus, EngineStepsEnum } from '@/store/Engine/constants';
import { formatEngineConfigDate } from '@/utils/util-functions';
import { EPButton } from '@/components';
import { EnvironmentEnum } from '@/constants/enums';

import {
  generateSetpoints,
  hasDeletedRules,
  mapRuleNames,
  migrateDeletedRulesToCustomRules,
} from './Engine/Common/EngineStep/EngineStep';
import Engine from './Engine/Engine';
import { shouldDisableDraftPublishing } from './EngineVersionUtils';
import FallbackOverview from './FallbackOverview/FallbackOverview';
import General from './General/General';
import RulesOverview from './RulesOverview/RulesOverview';
import { EngineVersionContainer } from './styled';
import RejectRecommendationModal from './Engine/Common/RejectRecommendationModal/RejectRecommendationModal';
import { getFormattedEngineVersion } from './VersionHistory/VersionHistory.utils';
import VersionAlert from './VersionHistory/VersionAlert';
import { versionTabs } from './Engine/CentrifugalCompressors/CentrifugalCompressors.utils';
import { EngineStore } from '@/store/Engine/EngineStore';
import StationStore from '@/store/Station/StationStore';
import DevicesStore from '@/store/Devices/DevicesStore';
import PermissionStore from '@/store/PermissionStore';
import SensorsKPITab from './Engine/CentrifugalCompressors/SensorsKPIConfiguration/SensorsKPITab';
import { StationControlType } from '@/store/Lifecycle/constants';

type NotificationType = 'success' | 'info' | 'warning' | 'error';

const EngineVersion = observer(
  ({
    setParentActiveTab,
    parentActiveTab,
    hasTriedToSubmit,
    setHasTriedToSubmit,
    confirmationModalInfo,
    setConfirmationModalInfo,
    isFooterHidden = false,
    fromVersionHistory = false,
    versionNumber = null,
  }) => {
    const {
      engineStore,
      stationStore,
      devicesStore,
      permissionStore,
    }: {
      engineStore?: EngineStore;
      stationStore?: StationStore;
      devicesStore?: DevicesStore;
      permissionStore?: PermissionStore;
    } = useStores();
    const { tvalue } = useMeasurement();
    const { t } = useTranslation();

    const [activeTab, setActiveTab] = useState(EngineTab.DefaultSetpoints);
    const [centrifugalActiveTab, setCentrifugalActiveTab] = useState(EngineTab.StationConfiguration);

    const [isRejectRecommendationModalOpen, setIsRejectRecommendationModalOpen] = useState(false);

    const [isRevertDefaultLoading, setIsRevertDefaultLoading] = useState(false);

    const [, setIsSaveDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showActiveModal, setShowActiveModal] = useState({
      ruleTab: false,
      fallbackTab: false,
    });
    const [disableSave, setDisableSave] = useState({});

    const [disablePublish, setDisablePublish] = useState({});

    const hasAcknowledgedRuleTab = engineStore.rangeAlerts?.hasAcknowledgedRule ?? true;
    const hasAcknowledgedFallbackTab = engineStore.rangeAlerts?.hasAcknowledgedFallback ?? true;
    const [isFlowFallbackAllowed, setIsFlowFallbackAllowed] = useState(true);
    
    const isNewDraft = !engineStore?.publishedEngineConfig?.version;

    const [api, contextHolder] = notification.useNotification();

    const openNotification = (type: NotificationType, message: string, description: string, duration = 5) => {
      api[type]({
        message: message,
        description: description,
        placement: 'bottomLeft',
        duration: duration,
      });
    };

    useEffect(() => {
      setHasTriedToSubmit(false);
    }, [stationStore.currentStation?.id]);

    useEffect(() => {
      setDisableSave(engineStore.validationMessages.length > 0);
    }, [engineStore.validationMessages]);

    useEffect(() => {
      setDisablePublish(!engineStore.hasDraftVersion);
    }, [engineStore.hasDraftVersion]);

    useEffect(() => {
      const { hasAcknowledgedRule, hasAcknowledgedFallback } = engineStore.rangeAlerts || {};

      const newShowActiveModal = {
        ruleTab:
          hasAcknowledgedRule === false &&
          engineStore.configurationStatus === EngineConfigStatus.Draft &&
          activeTab === EngineTab.DefaultSetpoints,
        fallbackTab:
          hasAcknowledgedFallback === false &&
          engineStore.configurationStatus === EngineConfigStatus.Draft &&
          activeTab === EngineTab.FallbackScenarios,
      };

      setShowActiveModal(newShowActiveModal);
    }, [engineStore.rangeAlerts, activeTab, engineStore.configurationStatus]);

    useEffect(() => {
      setIsFlowFallbackAllowed(stationStore.stationControlType !== StationControlType.NoFlowControl);
    }, [stationStore.stationControlType]);

    useEffect(() => {
      if (isNewDraft) return;

      const modalConfig = {
        [EngineTab.DefaultSetpoints]: {
          condition: showActiveModal.ruleTab,
          handler: 'ruleTab',
        },
        [EngineTab.FallbackScenarios]: {
          condition: showActiveModal.fallbackTab,
          handler: 'fallbackTab',
        },
      };

      const config = modalConfig[activeTab];
      if (config?.condition) {
        onConfirmationModalOpen(t('recommendationFlowLevelsChanged'), handleOnCloseModal(config.handler))();
      }
    }, [
      engineStore.mainRangesConfiguration,
      activeTab,
      hasAcknowledgedRuleTab,
      hasAcknowledgedFallbackTab,
      showActiveModal,
    ]);

    const centrifugalTabItems = [
      {
        label: t('stationParameters'),
        key: EngineTab.StationConfiguration,
        children: (
          <Engine
            setActiveTab={setCentrifugalActiveTab}
            initialStepName={EngineStepsEnum.StationConfiguration}
            hasTriedToSubmit={hasTriedToSubmit}
            setHasTriedToSubmit={setHasTriedToSubmit}
          />
        ),
      },
      {
        label: t('compressorParameters'),
        key: EngineTab.CompressorParameters,
        children: (
          <Engine
            setActiveTab={setCentrifugalActiveTab}
            initialStepName={EngineStepsEnum.CompressorParametersConfiguration}
            hasTriedToSubmit={hasTriedToSubmit}
            setHasTriedToSubmit={setHasTriedToSubmit}
          />
        ),
      },
      ...(!engineStore.isReadOnly && import.meta.env.VITE_ENV !== EnvironmentEnum.Production && !isFooterHidden
        ? [
            {
              label: t('general'),
              key: EngineTab.General,
              children: <General setActiveTab={setParentActiveTab} />,
            },
          ]
        : []),
    ];

    const tabItems = [
      {
        label: t('rulesOverview'),
        key: EngineTab.DefaultSetpoints,
        children: <RulesOverview hasTriedToSubmit={hasTriedToSubmit} activeParentTab={parentActiveTab} />,
      },
      {
        label: t('compressorGroups'),
        key: EngineTab.CompressorGroups,
        children: (
          <Engine
            setActiveTab={setActiveTab}
            initialStepName={EngineStepsEnum.CompressorGroups}
            hasTriedToSubmit={hasTriedToSubmit}
            setHasTriedToSubmit={setHasTriedToSubmit}
          />
        ),
      },
      {
        label: t('fallbackOverview'),
        key: EngineTab.FallbackScenarios,
        children: (
          <FallbackOverview 
            setHasTriedToSubmit={setHasTriedToSubmit} 
            hasTriedToSubmit={hasTriedToSubmit}
            fromVersionHistory={fromVersionHistory}
            versionNumber={versionNumber}
          />
        ),
      },
      {
        label: t('sensorsSelection'),
        className: 'kpi-container',
        key: EngineTab.SensorsConfiguration,
        children: (
        <SensorsKPITab
        setIsSaveDisabled={setIsSaveDisabled} 
        activeTab={activeTab} 
        isPressureFallbackAllowed={true}
        isFlowFallbackAllowed={ isFlowFallbackAllowed }
        />
        ), 
      },
      {
        label: t('mainRanges'),
        key: EngineTab.MainRangesConfiguration,
        children: (
          <Engine
            setActiveTab={setActiveTab}
            initialStepName={EngineStepsEnum.MainRangesConfiguration}
            hasTriedToSubmit={hasTriedToSubmit}
            setHasTriedToSubmit={setHasTriedToSubmit}
          />
        ),
      },
      {
        label: t('defaultSetpoints'),
        key: EngineTab.DefaultSetpointsConfiguration,
        children: (
          <Engine
            setActiveTab={setActiveTab}
            initialStepName={EngineStepsEnum.DefaultSetpointsConfiguration}
            hasTriedToSubmit={hasTriedToSubmit}
            setHasTriedToSubmit={setHasTriedToSubmit}
          />
        ),
      },

      ...(!engineStore.isReadOnly && import.meta.env.VITE_ENV !== EnvironmentEnum.Production && !isFooterHidden
        ? [
            {
              label: t('general'),
              key: EngineTab.General,
              children: <General setActiveTab={setParentActiveTab} />,
            },
          ]
        : []),
    ];

    const onTabChange = (tab: EngineTab) => {
      setHasTriedToSubmit(false);
      setActiveTab(tab);
      setCentrifugalActiveTab(tab);
    };

    const handleOnCloseModal = (tabType: string) => () => {
      onConfirmationModalClose();
      setShowActiveModal(prevState => ({
        ...prevState,
        [tabType]: false,
      }));
      engineStore.updateRangeAlerts({
        ...(tabType === 'ruleTab' && { hasAcknowledgedRule: true }),
        ...(tabType === 'fallbackTab' && { hasAcknowledgedFallback: true }),
      });
    };

    const onPreSave = async () => {
      if (engineStore.mainRangesConfiguration.hasChanges()) {
        engineStore.mainRangesConfiguration?.pressureLevels?.copyPressureRanges(
          engineStore.mainRangesConfiguration.flowLevels?.ranges,
        );

        setShowActiveModal({
          ruleTab: true,
          fallbackTab: true,
        });
      }

      if (hasDeletedRules(engineStore)) {
        onConfirmationModalOpen(t('changesInMainRangesAffectFallbackRules'), onSave)();
      } else onSave();
    };

    const onSave = async () => {
      try {
        engineStore.error = null;
        setIsLoading(true);
        setHasTriedToSubmit(true);
        engineStore.clearValidationMessages();

        engineStore.validateAll(tvalue);

        if (engineStore.validationMessages?.length) {
          const activeTab = engineStore.validationMessages?.[0]?.tab;
          setActiveTab(activeTab as EngineTab);
          setCentrifugalActiveTab(activeTab as EngineTab);
          setIsLoading(false);
          message.error(t(engineStore.validationMessages?.[0]?.message));
          return;
        }

        const hasMainRangeChanges = engineStore.mainRangesConfiguration.hasChanges();
        const hasSetpointChanges = engineStore.defaultSetpointsConfiguration.hasChanges();

        if (hasMainRangeChanges) {
          engineStore.mainRangesConfiguration?.pressureLevels?.copyPressureRanges(
            engineStore.mainRangesConfiguration.flowLevels?.ranges,
          );

          await engineStore.updateRangeAlerts({
            hasAcknowledgedFallback: false,
            hasAcknowledgedRule: false,
          });

          setShowActiveModal({
            ruleTab: true,
            fallbackTab: true,
          });
        }

        if (hasMainRangeChanges || hasSetpointChanges) {
          migrateDeletedRulesToCustomRules(engineStore);
          generateSetpoints(engineStore);
          mapRuleNames(engineStore);
        }

        await engineStore.saveEngineConfig(tvalue);

        setIsLoading(false);
        onConfirmationModalClose();
      } catch (error) {
        setIsLoading(false);
        message.error(t('errorSavingEngineConfig'));
      }
    };
    const onConfirmationModalOpen = (modalText, actionHandler) => () =>
      setConfirmationModalInfo({ open: true, modalText, actionHandler });

    const onConfirmationModalClose = () =>
      setConfirmationModalInfo({ open: false, modalText: undefined, actionHandler: undefined });

    const onPublishDraftHandler = async () => {
      await engineStore.publishDraft(tvalue);
      onConfirmationModalClose();
      setParentActiveTab(versionTabs.published);
    };

    const onPublishRecommendationHandler = async () => {
      window.localStorage.removeItem('hasVisitedRecommendedTab');
      window.localStorage.removeItem('hasViewedApprovalReport');
      await engineStore.publishRecommendation(tvalue);
      onConfirmationModalClose();
      setParentActiveTab(versionTabs.published);
      await engineStore.updateRangeAlerts({
        hasAcknowledgedFallback: true,
        hasAcknowledgedRule: true,
      });
    };

    const onRevertToPublishedHandler = async () => {
      try {
        await engineStore.revertToPublished(tvalue);
        onConfirmationModalClose();
      } catch (e) {
        onConfirmationModalClose();
        setParentActiveTab(versionTabs.published);
      }
    };

    const onCancelClick = () => engineStore.cancelChanges();

    const onApprovalReportClick = async () => {
      if (!window.localStorage.getItem('hasViewedApprovalReport')) {
        await engineStore.viewApprovalReport();
        window.localStorage.setItem('hasViewedApprovalReport', 'true');
      }
    };

    const onPublishErrorHandler = () => {
      onConfirmationModalClose();
    };

    const onPublishRecommendationClick = () => {
      const { disabled, field, table, compressor } = shouldDisableDraftPublishing(engineStore, devicesStore);

      // TODO: added validations blocked Moti from publishing the rules
      // TODO: for now we disabled all validations upon Draft Publishing
      // TODO: upon receiving new validation ticket, make sure to implement properly and verify properly
      // eslint-disable-next-line no-constant-condition
      if (disabled && false && engineStore.configurationStatus === EngineConfigStatus.Draft) {
        return onConfirmationModalOpen(
          t('missingMandatoryFieldValue', { field, table, compressor }),
          onPublishErrorHandler,
        );
      }

      if (engineStore.configurationStatus === EngineConfigStatus.Recommendation) {
        return onConfirmationModalOpen(
          t('publishRecommendationConfirmation', {
            draftVersionText: engineStore.draftEngineConfig?.version
              ? t('draftVersionText', {
                  draftVersion: `${getFormattedEngineVersion(engineStore.draftEngineConfig?.version)}`,
                })
              : '',
          }),
          onPublishRecommendationHandler,
        );
      } else {
        return onConfirmationModalOpen(
          t(
            engineStore.engineVersion === ENGINE_VERSION_1_0
              ? 'publishConfirmationEngine1.0'
              : 'publishDraftConfirmation',
          ),
          onPublishDraftHandler,
        );
      }
    };

    const onRevertToBaselineHandler = async () => {
      setIsRevertDefaultLoading(true);

      if (await engineStore.prepareDefaultStrategy(true, tvalue)) {
        await engineStore.getEngineConfig(tvalue);
        setIsRevertDefaultLoading(false);
      } else {
        openNotification(t('warning'), t('noBaseline'), t('noDefaultBaselineStrategy'), 5);
      }
      onConfirmationModalClose();
      setIsRevertDefaultLoading(false);
    };

    const onRevertToBaselineClick = () => {
      onConfirmationModalOpen(t('revertToDefaultBaselineStrategy'), onRevertToBaselineHandler)();
    };

    const onRejectRecommendationClick = () => {
      setIsRejectRecommendationModalOpen(true);
    };

    const recommendationTabBarExtraContent =
      engineStore.configurationStatus === EngineConfigStatus.Recommendation
        ? {
            left: (
              <span className="created-on-container">
                <span className="created-on-label">{t('createdOn')}</span>
                <span>{formatEngineConfigDate(engineStore.automaticEngineConfig?.createdDate)}</span>
              </span>
            ),
            right: (
              <a
                className="approval-report"
                target="_blank"
                rel="noreferrer"
                href={engineStore.automaticEngineConfig.approvalReport}
                onClick={onApprovalReportClick}
              >
                {t('approvalReport')}
              </a>
            ),
          }
        : null;

    return (
      <EngineVersionContainer
        $configurationStatus={engineStore.configurationStatus}
        className={engineStore.engineVersion === ENGINE_VERSION_1_0 ? 'has-version-alert' : ''}
      >
        <div className="engine-version-one-alert-container">
          <VersionAlert engineVersion={engineStore.engineVersion} />
        </div>
        {contextHolder}
        <div className={`engine-tabs-container ${engineStore.configurationStatus === EngineConfigStatus.Recommendation ? 'recommendation-container' : ''}`}>
          <Tabs
            activeKey={engineStore.isCentrifugalOnlyStation ? centrifugalActiveTab : activeTab}
            onChange={onTabChange}
            animated={{
              inkBar: true,
              tabPane: false,
            }}
            items={engineStore.isCentrifugalOnlyStation ? centrifugalTabItems : tabItems}
            moreIcon={false}
            className="engine-version-tabs"
            tabBarExtraContent={recommendationTabBarExtraContent}
          />
        </div>
        {engineStore.configurationStatus !== EngineConfigStatus.Published && !isFooterHidden && (
          <div className="footer-publish-buttons">
            {engineStore.configurationStatus !== EngineConfigStatus.Recommendation && (
              <>
                <EPButton
                  type="primary"
                  size="medium"
                  onClick={onCancelClick}
                  className="back-button"
                  disabled={engineStore.isReadOnly || !engineStore.hasChanges() || isLoading}
                >
                  <span className="button-label">{t('cancel')}</span>
                </EPButton>
                <EPButton
                  type="primary"
                  size="medium"
                  className="footer-publish-button"
                  disabled={
                    engineStore.isReadOnly ||
                    isEmpty(engineStore.publishedEngineConfig) ||
                    engineStore.areDraftAndPublishedEqual ||
                    engineStore.hasPIDChanges ||
                    engineStore.hasKpiChanges ||
                    isLoading
                  }
                  onClick={onConfirmationModalOpen(t('revertToPublishedConfirmation'), onRevertToPublishedHandler)}
                >
                  <span className="button-label">{t('revertToPublished')}</span>
                </EPButton>

                <EPButton
                  type="primary"
                  size="medium"
                  className="footer-publish-button"
                  onClick={onRevertToBaselineClick}
                  disabled={engineStore.isReadOnly}
                >
                  <span className="button-label">{t('Revert to Baseline')}</span>
                </EPButton>

                <EPButton
                  type="primary"
                  size="medium"
                  className="footer-publish-button"
                  onClick={onPreSave}
                  disabled={
                    engineStore.isReadOnly ||
                    !engineStore.hasChanges() ||
                    isLoading ||
                    (engineStore.isCentrifugalOnlyStation && disableSave)
                  }
                >
                  <span className="button-label">{t('save')}</span>
                </EPButton>
              </>
            )}
            {engineStore.configurationStatus === EngineConfigStatus.Recommendation && (
              <EPButton
                type="primary"
                size="medium"
                disabled={permissionStore.isAuth(permissions.UPDATE_STATION_ENGINE) === false}
                className="footer-publish-button"
                onClick={onRejectRecommendationClick}
              >
                <span className="button-label">{t('Reject')}</span>
              </EPButton>
            )}

            <EPButton
              type="primary"
              size="medium"
              disabled={
                permissionStore.isAuth(permissions.UPDATE_STATION_ENGINE) === false ||
                (engineStore.isCentrifugalOnlyStation && disablePublish)
              }
              className="footer-publish-button"
              onClick={onPublishRecommendationClick()}
            >
              <span className="button-label">{t('publish')}</span>
            </EPButton>
          </div>
        )}
        <Modal
          open={!!confirmationModalInfo?.open}
          onOk={confirmationModalInfo?.actionHandler}
          onCancel={onConfirmationModalClose}
          okText={
            shouldDisableDraftPublishing(engineStore, devicesStore).disabled ||
            showActiveModal.ruleTab ||
            showActiveModal.fallbackTab
              ? t('ok')
              : t('yes')
          }
          cancelText={t('cancel')}
          centered
          confirmLoading={isRevertDefaultLoading}
          maskClosable={false}
          closable={false}
          {...(engineStore.engineVersion === ENGINE_VERSION_1_0 &&
            isNil(confirmationModalInfo?.actionHandler) && {
              footer: null,
              closable: true,
              maskClosable: true,
            })}
        >
          <span>{confirmationModalInfo?.modalText}</span>
        </Modal>

        <RejectRecommendationModal
          isOpen={isRejectRecommendationModalOpen}
          setIsOpen={setIsRejectRecommendationModalOpen}
        />
      </EngineVersionContainer>
    );
  },
);

export default EngineVersion;
